import { devBus } from '../debug/devBus';

// Top-center floating pill: outside/inside view toggle. The dev panel also
// exposes this (top button) but is hidden behind ?dev=1, so this is the only
// player-facing way to navigate in/out of the room.
//
// External mutations (dev panel button, keyboard shortcut, programmatic) flow
// back via the polling tick so the label stays in sync.
//
// Interactivity notes:
//  - Entire `<button>` element is the click target (including padding +
//    transparent border). Hover/active CSS makes that explicit so the whole
//    pill reads as tappable, not just the arrow text.
//  - `pointer-events: auto` is defensive against any future overlay that
//    might inherit `none` from a parent stylesheet.
export function createViewToggle(
  getView: () => 'exterior' | 'interior',
  toggle: () => void,
  /** N8 — the generator gate: while this reads false, going INSIDE is refused
   *  (the shop is dark; start the generator). Going back outside is never
   *  gated. Absent → entry always open. */
  canEnter?: () => boolean,
  /** Demo first-launch: while exterior, HIDE this button entirely — the story
   *  director's "step inside" pill carries entry in the action slot. Once inside,
   *  the button returns as "go outside" (the persistent nav). Absent → always shown. */
  suppressEntry?: boolean,
): void {
  const btn = document.createElement('button');
  btn.type = 'button';
  const baseCss = [
    'position: fixed',
    'top: 16px',
    'left: 50%',
    'transform: translateX(-50%)',
    'padding: 12px 28px',           // larger touch / click target
    // Legibility (John, 2026-06-29: larger + brighter, hard to see): bright text
    // on darker glass reads over any scene. The resting bg comes from the Look
    // objects below (NEUTRAL dark / ALIVE warm-dark); this is the base.
    'background: rgba(20, 26, 38, 0.62)',
    'color: #f4f8ff',
    'border: 1px solid rgba(150, 178, 228, 0.42)',
    'border-radius: 14px',
    'cursor: pointer',
    'font: 700 15px/1.2 system-ui, sans-serif',
    'letter-spacing: 0.02em',
    'text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55)',
    'backdrop-filter: blur(6px)',
    '-webkit-backdrop-filter: blur(6px)',
    'z-index: 1000',
    'user-select: none',
    'min-width: 160px',
    'text-align: center',
    'pointer-events: auto',
    'touch-action: manipulation',   // no 300ms iOS tap delay, no double-tap zoom
    'transition: background 120ms ease, border-color 120ms ease, transform 120ms ease, box-shadow 200ms ease',
  ];
  btn.style.cssText = baseCss.join('; ');

  // ── Three resting looks, so the pill always reads its own status ──────────
  //   GATED   — dark shop: dim + not-allowed (the goal shown, not yet reachable)
  //   ALIVE   — powered exterior: the way IN, lit warm so the eye goes there
  //   NEUTRAL — inside: plain glass "← go outside"
  type Look = { bg: string; border: string; glow: string };
  const NEUTRAL: Look = { bg: 'rgba(20,26,38,0.62)', border: 'rgba(150,178,228,0.42)', glow: 'none' };
  const NEUTRAL_HOVER: Look = { bg: 'rgba(32,40,56,0.74)', border: 'rgba(150,178,228,0.62)', glow: 'none' };
  // Warm lamp glow — the generator just lit the shop, so the doorway invites. The
  // bg stays dark glass (for bright-text legibility); the warmth lives in the
  // border + glow so "alive" still reads without sacrificing contrast.
  const ALIVE: Look = { bg: 'rgba(46,32,16,0.62)', border: 'rgba(255,201,135,0.95)', glow: '0 0 18px rgba(255,188,120,0.55)' };
  const ALIVE_HOVER: Look = { bg: 'rgba(60,42,22,0.72)', border: 'rgba(255,214,160,1)', glow: '0 0 24px rgba(255,196,130,0.78)' };
  const apply = (s: Look): void => {
    btn.style.background = s.bg;
    btn.style.borderColor = s.border;
    btn.style.boxShadow = s.glow;
  };

  // N8: entering is gated on power; the refusal is legible, not just inert.
  const entryBlocked = (): boolean => getView() === 'exterior' && !!canEnter && !canEnter();
  // ALIVE = powered AND still outside (the way in is the actionable invitation).
  const isAlive = (): boolean => getView() === 'exterior' && !entryBlocked();
  let hovering = false;

  const paint = (): void => {
    // Demo first launch: while exterior, keep the top button HIDDEN — the
    // step-inside pill owns entry. It returns as "go outside" once inside.
    if (suppressEntry && getView() === 'exterior') {
      btn.style.opacity = '0';
      btn.style.pointerEvents = 'none';
      return;
    }
    btn.style.pointerEvents = 'auto';
    if (entryBlocked()) {
      // Show the GOAL ("go inside") dimmed, not a second "the shop is dark" — the
      // story card already carries that instruction, so the button + card read as
      // one unified start state (button = where you're headed, card = what unlocks it).
      btn.textContent = 'go inside →';
      btn.title = 'the shop is dark — start the generator by the door';
      btn.style.opacity = '0.5';
      btn.style.cursor = 'not-allowed';
      apply(NEUTRAL);
      return;
    }
    btn.style.opacity = '1';
    btn.style.cursor = 'pointer';
    if (isAlive()) {
      btn.textContent = 'go inside →';
      btn.title = 'the generator is running — step inside';
      apply(hovering ? ALIVE_HOVER : ALIVE);
    } else {
      btn.textContent = '← go outside';
      btn.title = '';
      apply(hovering ? NEUTRAL_HOVER : NEUTRAL);
    }
  };
  paint();

  // Hover + active feedback so the entire pill visibly reacts — not just the
  // text/arrow. paint() owns the resting look per state, so hover just re-paints
  // (brighter variant) and leave restores; the gated state never brightens.
  btn.addEventListener('pointerenter', () => { hovering = true; if (!entryBlocked()) paint(); });
  btn.addEventListener('pointerleave', () => { hovering = false; btn.style.transform = 'translateX(-50%)'; paint(); });
  btn.addEventListener('pointerdown', () => { btn.style.transform = 'translateX(-50%) scale(0.97)'; });
  btn.addEventListener('pointerup', () => { btn.style.transform = 'translateX(-50%)'; });

  btn.addEventListener('click', () => {
    if (entryBlocked()) { paint(); return; } // the dark shop refuses
    toggle();
    paint();
  });
  document.body.appendChild(btn);

  // Item 1 — the generator powering the shop must make the way IN visibly come
  // ALIVE, not just un-dim on the poll. On the power:on latch flip (demo: the
  // generator pull) snap to the lit style + a one-time glow pulse to pull the
  // eye to the door. Dev boots pre-powered BEFORE this subscribes, so the pulse
  // is a demo-only first-run beat; the lit resting style still applies in dev.
  devBus.on('power:on', () => {
    if (getView() !== 'exterior') return; // already inside — no doorway to invite toward
    paint(); // → ALIVE lit resting style immediately
    // A finite attention pulse (two warm swells), then it settles to ALIVE's
    // resting glow (the inline boxShadow paint() just set; the anim has no fill).
    btn.animate(
      [
        { boxShadow: '0 0 0 0 rgba(255,188,120,0)', transform: 'translateX(-50%) scale(1)' },
        { boxShadow: '0 0 30px 8px rgba(255,196,130,0.92)', transform: 'translateX(-50%) scale(1.08)' },
        { boxShadow: '0 0 16px 0 rgba(255,188,120,0.5)', transform: 'translateX(-50%) scale(1)' },
      ],
      { duration: 1000, easing: 'ease-out', iterations: 2 },
    );
  });

  // External mutations (dev panel toggleView, future keyboard shortcut) —
  // 100ms polling matches the dev panel's mirror loop, cheap and uncomplicated.
  setInterval(paint, 100);
}
